import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  ArrowBack,
  Add,
  EditOutlined,
  DeleteOutline,
  LockOutlined,
  SpeedOutlined,
  RouteOutlined,
  LocalGasStationOutlined,
  CurrencyRupeeOutlined,
  NotesOutlined,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { VehicleIcon } from './FuelEfficiency';
import {
  calculateMileage,
  calculateEfficiencyStats,
  toDateString,
  fromDateString,
  formatFuelDate,
  formatRupees,
  formatNumber,
} from '../../utils/fuelEfficiency';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';
import AmountField from '../../components/AmountField';

const emptyForm = () => ({
  date: new Date(),
  odometer: '',
  amount: '',
  quantity: '',
  isFullTank: true,
  notes: '',
});

// Price per litre derived from amount ÷ quantity (2 decimals), or '' if not computable
const derivePrice = (amount, quantity) => {
  const price = parseFloat(amount) / parseFloat(quantity);
  return amount && quantity && isFinite(price) && price > 0 ? price.toFixed(2) : '';
};

const StatTile = ({ icon, color, value, label }) => (
  <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: (t) => alpha(t.palette[color].main, 0.06), minWidth: 0 }}>
    <Box sx={{ color: `${color}.main`, display: 'flex', mb: 0.75, '& svg': { fontSize: 22 } }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.125rem', sm: '1.375rem' }, fontVariantNumeric: 'tabular-nums' }} noWrap>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

const VehicleDetails = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { supabase, user } = useSupabase();

  const [vehicle, setVehicle] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const detailsTour = useTour('vehicle-details', { ready: !loading && !notFound });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteEntry, setDeleteEntry] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [vehicleResult, entriesResult] = await Promise.all([
      supabase.from('vehicles').select('*').eq('id', vehicleId).eq('user_id', user.id).maybeSingle(),
      supabase
        .from('fuel_entries')
        .select('*')
        .eq('vehicle_id', vehicleId)
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false }),
    ]);

    if (vehicleResult.error) console.error('Error fetching vehicle:', vehicleResult.error);
    if (entriesResult.error) {
      console.error('Error fetching fuel entries:', entriesResult.error);
      notify('Failed to fetch fuel entries', 'error');
    }
    setVehicle(vehicleResult.data || null);
    setNotFound(!vehicleResult.data);
    setEntries(entriesResult.data || []);
    setLoading(false);
  }, [supabase, user, vehicleId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(() => calculateEfficiencyStats(entries), [entries]);
  const baselineId = entries.length ? entries[entries.length - 1].id : null;

  // The first (oldest) entry is the baseline for every mileage calculation:
  // it must be a full tank and can be edited but not deleted.
  const fullTankLocked = entries.length === 0 || (editingEntry && editingEntry.id === baselineId);
  const pricePerUnit = derivePrice(form.amount, form.quantity);

  // ---- Add / edit ----
  const openAddDialog = () => {
    setEditingEntry(null);
    setForm(emptyForm());
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setForm({
      date: fromDateString(entry.date),
      odometer: String(entry.odometer_reading),
      amount: String(entry.total_cost),
      quantity: String(entry.fuel_quantity),
      isFullTank: entry.is_full_tank,
      notes: entry.notes || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingEntry(null);
  };

  const updateField = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setFormError('');
  };

  const handleSave = async () => {
    const odometer = parseFloat(form.odometer);
    const amount = parseFloat(form.amount);
    const quantity = parseFloat(form.quantity);

    if (!form.odometer || !form.amount || !form.quantity) {
      setFormError('Please enter odometer reading, total amount, and fuel quantity');
      return;
    }
    if (!(odometer >= 0) || !(amount > 0) || !(quantity > 0)) {
      setFormError('Odometer, amount and quantity must be valid positive numbers');
      return;
    }
    if (!form.date || isNaN(form.date.getTime())) {
      setFormError('Please choose a valid date');
      return;
    }
    // A new fill-up on/after the latest one must show a higher odometer reading,
    // otherwise distance (and every mileage figure) goes negative.
    const latest = entries[0];
    if (!editingEntry && latest && toDateString(form.date) >= latest.date.slice(0, 10)
      && odometer <= latest.odometer_reading) {
      setFormError(`Odometer must be higher than the last reading (${formatNumber(latest.odometer_reading)} km on ${formatFuelDate(latest.date)}).`);
      return;
    }

    setSaving(true);
    const entryData = {
      vehicle_id: vehicleId,
      user_id: user.id,
      date: toDateString(form.date),
      odometer_reading: odometer,
      fuel_quantity: quantity,
      fuel_price_per_unit: amount / quantity,
      total_cost: amount,
      fuel_type: editingEntry?.fuel_type || 'Petrol',
      is_full_tank: fullTankLocked ? true : form.isFullTank,
      notes: form.notes.trim() || null,
    };

    const { error } = editingEntry
      ? await supabase.from('fuel_entries').update(entryData).eq('id', editingEntry.id)
      : await supabase.from('fuel_entries').insert(entryData);
    setSaving(false);

    if (error) {
      console.error('Error saving fuel entry:', error);
      setFormError('Failed to save fuel entry');
      return;
    }

    setDialogOpen(false);
    notify(editingEntry ? 'Fuel entry updated successfully' : 'Fuel entry added successfully');
    setEditingEntry(null);
    fetchData();
  };

  // ---- Delete ----
  const handleDelete = async () => {
    const entry = deleteEntry;
    setDeleteEntry(null);
    if (!entry || entry.id === baselineId) return;
    const { error } = await supabase.from('fuel_entries').delete().eq('id', entry.id);
    if (error) {
      console.error('Error deleting entry:', error);
      notify('Failed to delete fuel entry', 'error');
      return;
    }
    notify('Fuel entry deleted successfully');
    fetchData();
  };

  // ---- Rendering helpers ----
  const rows = entries.map((entry, index) => ({
    entry,
    mileage: calculateMileage(entry, index < entries.length - 1 ? entries[index + 1] : null),
    isBaseline: entry.id === baselineId,
  }));

  const entryActions = ({ entry, isBaseline }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
      <Tooltip title="Edit entry">
        <IconButton size="small" onClick={() => openEditDialog(entry)} aria-label="Edit entry">
          <EditOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      {isBaseline ? (
        <Tooltip title="The first entry is the baseline for mileage calculations and can't be deleted. Edit it instead.">
          <span>
            <IconButton size="small" disabled aria-label="Baseline entry cannot be deleted">
              <LockOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title="Delete entry">
          <IconButton size="small" onClick={() => setDeleteEntry(entry)} aria-label="Delete entry" sx={{ '&:hover': { color: 'error.main' } }}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const mileageChip = (mileage) =>
    mileage ? <Chip label={`${mileage.toFixed(2)} km/l`} size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} /> : null;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (notFound) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" action={<Button onClick={() => navigate('/utilities/fuel-efficiency')}>Back</Button>}>
          Vehicle not found.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <IconButton onClick={() => navigate('/utilities/fuel-efficiency')} aria-label="Back to vehicles">
            <ArrowBack />
          </IconButton>
          <Box sx={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: (t) => alpha(t.palette.primary.main, 0.08), color: 'primary.main' }}>
            <VehicleIcon type={vehicle.vehicle_type} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>{vehicle.name}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[vehicle.vehicle_number, vehicle.vehicle_type].filter(Boolean).join(' · ')}
              {vehicle.is_archived && ' · Archived'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TourButton onClick={detailsTour.start} />
          <Button variant="contained" startIcon={<Add />} onClick={openAddDialog} data-tour="fuel-add-entry">Add Fuel Entry</Button>
        </Box>
      </Box>

      {/* Efficiency summary */}
      {stats ? (
        <Paper data-tour="fuel-summary" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Fuel Efficiency Summary</Typography>
            <Typography variant="body2" color="text.secondary">
              Total spent <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatRupees(stats.totalCost)}</Box>
              {' · '}{stats.entryCount} fill-ups
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 2 } }}>
            <StatTile icon={<SpeedOutlined />} color="primary" value={formatNumber(stats.avgMileage, 2)} label="Avg km/l" />
            <StatTile icon={<RouteOutlined />} color="success" value={formatNumber(stats.totalDistance)} label="Total km" />
            <StatTile icon={<LocalGasStationOutlined />} color="info" value={`${formatNumber(stats.totalFuel, 2)} L`} label="Fuel (full-tank fills)" />
            <StatTile icon={<CurrencyRupeeOutlined />} color="warning" value={formatRupees(stats.costPerKm)} label="Cost / km" />
          </Box>
        </Paper>
      ) : entries.length === 1 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Baseline recorded. Add your next fill-up (ideally a full tank) to start seeing mileage and cost per km.
        </Alert>
      ) : null}

      {/* Entries */}
      {entries.length === 0 ? (
        <Card sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <Box sx={{ width: 88, height: 88, borderRadius: '50%', mx: 'auto', mb: 2, display: 'grid', placeItems: 'center', bgcolor: (t) => alpha(t.palette.primary.main, 0.08), color: 'primary.main' }}>
            <SpeedOutlined sx={{ fontSize: 44 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>No Fuel Entries</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            Start tracking fuel efficiency by adding your first fuel entry.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>Add Fuel Entry</Button>
        </Card>
      ) : (
        <Paper data-tour="fuel-entries">
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Fuel Entries</Typography>
          </Box>

          {isMobile ? (
            <Box>
              {rows.map((row) => (
                <Box key={row.entry.id} sx={{ px: 2, py: 1.75, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatFuelDate(row.entry.date)}
                        {row.isBaseline && <Chip label="Baseline" size="small" sx={{ ml: 1, height: 20 }} />}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatNumber(row.entry.odometer_reading)} km · {formatNumber(row.entry.fuel_quantity, 2)} L · {formatRupees(row.entry.fuel_price_per_unit)}/L
                        {!row.entry.is_full_tank && ' · Partial fill'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatRupees(row.entry.total_cost)}</Typography>
                      {mileageChip(row.mileage)}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                      {row.entry.notes || ''}
                    </Typography>
                    {entryActions(row)}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Odometer</TableCell>
                    <TableCell align="right">Fuel</TableCell>
                    <TableCell align="right">Price / L</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Fill</TableCell>
                    <TableCell align="right">Mileage</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.entry.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatFuelDate(row.entry.date)}
                        {row.isBaseline && <Chip label="Baseline" size="small" sx={{ ml: 1, height: 20 }} />}
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.entry.odometer_reading)} km</TableCell>
                      <TableCell align="right">{formatNumber(row.entry.fuel_quantity, 2)} L</TableCell>
                      <TableCell align="right">{formatRupees(row.entry.fuel_price_per_unit)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatRupees(row.entry.total_cost)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={row.entry.is_full_tank ? 'text.primary' : 'text.secondary'}>
                          {row.entry.is_full_tank ? 'Full tank' : 'Partial'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{mileageChip(row.mileage) || <Typography variant="body2" color="text.disabled">–</Typography>}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {row.entry.notes ? (
                          <Tooltip title={row.entry.notes}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', minWidth: 0 }}>
                              <NotesOutlined sx={{ fontSize: 16, flexShrink: 0 }} />
                              <Typography variant="body2" noWrap>{row.entry.notes}</Typography>
                            </Box>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{entryActions(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Add / edit fuel entry */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingEntry ? 'Edit Fuel Entry' : 'Add Fuel Entry'}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: 1 }}>
              <DatePicker
                label="Date *"
                value={form.date}
                onChange={(value) => { setForm({ ...form, date: value }); setFormError(''); }}
                maxDate={new Date()}
                format="dd MMM yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                label="Odometer Reading"
                required
                type="number"
                inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                placeholder="e.g., 15000"
                InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                value={form.odometer}
                onChange={updateField('odometer')}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <AmountField
                  label="Total Amount"
                  required
                  type="number"
                  inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                  placeholder="4000"
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  value={form.amount}
                  onChange={updateField('amount')}
                />
                <TextField
                  label="Fuel Quantity"
                  required
                  type="number"
                  inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                  placeholder="40.5"
                  InputProps={{ endAdornment: <InputAdornment position="end">L</InputAdornment> }}
                  value={form.quantity}
                  onChange={updateField('quantity')}
                />
              </Box>
              <TextField
                label="Price per Litre (auto-calculated)"
                value={pricePerUnit}
                placeholder="Enter amount and quantity"
                InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.default' } }}
              />
              <Box>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={fullTankLocked ? true : form.isFullTank}
                      disabled={fullTankLocked}
                      onChange={(e) => setForm({ ...form, isFullTank: e.target.checked })}
                    />
                  )}
                  label={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      Full Tank{fullTankLocked && ' (Required)'}
                      {fullTankLocked && <LockOutlined sx={{ fontSize: 16, color: 'success.main' }} />}
                    </Box>
                  )}
                />
                {fullTankLocked ? (
                  <Alert severity="success" icon={false} sx={{ py: 0.25 }}>
                    The first entry is always a full tank so mileage can be calculated accurately.
                  </Alert>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    Mileage is calculated between full-tank fill-ups.
                  </Typography>
                )}
              </Box>
              <TextField
                label="Notes (Optional)"
                multiline
                minRows={2}
                placeholder="Add any notes…"
                value={form.notes}
                onChange={updateField('notes')}
              />
              {formError && <Alert severity="error">{formError}</Alert>}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingEntry ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteEntry)} onClose={() => setDeleteEntry(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Fuel Entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the fuel entry from {deleteEntry ? formatFuelDate(deleteEntry.date) : ''}
            {deleteEntry ? ` (${formatRupees(deleteEntry.total_cost)})` : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEntry(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <GuidedTour steps={TOURS['vehicle-details']} open={detailsTour.open} onClose={detailsTour.close} />
    </Box>
  );
};

export default VehicleDetails;
